import { APP_INITIALIZER, ModuleWithProviders, NgModule } from "@angular/core";
import { EffectsModule } from "@ngrx/effects";
import { NgrxUtilsModule } from "@obg/ngrx-utils";
import { GlobalModule } from "@obg/ngx.global";
import { RouterModule } from "@obg/ngx.router";
import { isFunction } from "lodash-es";

import { AamsDataEffect } from "./aams-data/aams-data.effect";
import { AppContextEffect } from "./app-context/app-context.effect";
import { SessionEffect } from "./auth/session.effect";
import { AuthTransactionEffect } from "./auth-transaction/auth-transaction.effect";
import { DgaAuthTransactionEffect } from "./auth-transaction/dga/dga-auth-transaction.effect";
import { GoogleAuthTransactionEffect } from "./auth-transaction/google/google-auth-transaction.effect";
import { OpenIdConnectService } from "./auth-transaction/open-id-connect/open-id-connect.service";
import { SmartIdAuthTransactionEffect } from "./auth-transaction/smart-id/smart-id-auth-transaction.effect";
import { CommandDispatcherDirective } from "./command-dispatcher/command-dispatcher.directive";
import { _CORE_CONFIG, CORE_CONFIG } from "./config/const";
import { CoreConfig } from "./config/core-config";
import { CoreConfigModel } from "./config/model";
import { CurrencyEffect } from "./currency/currency.effect";
import { CustomerComplianceEffect } from "./customer-compliance/customer-compliance.effect";
import { KycActionsEffect } from "./customer-compliance/kyc-actions.effect";
import { CustomerManagementConfigService } from "./customer-management/customer-management.config";
import { CustomerManagementEffect } from "./customer-management/customer-management.effect";
import { CustomerManagementService } from "./customer-management/customer-management.model";
import { CoreCustomerManagementService } from "./customer-management/customer-management.service";
import { NoopCustomerManagementService } from "./customer-management/noop/noop-customer-management.service";
import { DocumentMetaEffect } from "./document-meta/document-meta.effect";
import { GameSessionEffect } from "./game-sessions/game-session.effect";
import { CoreHttpClient } from "./http-client/http-client.service";
import { JurisdictionEffect } from "./jurisdiction/jurisdiction.effect";
import { JurisdictionTelemetryEffect } from "./jurisdiction/jurisdiction.telemetry.effect";
import { JurisdictionPrefixModule } from "./jurisdiction/utils/jurisdiction-prefix.module";
import { LastVisitedProductEffect } from "./last-visited-product/last-visited-product.effect";
import { LimitsReminderEffect } from "./limits-reminder/limits-reminder.effect";
import { PageEffect } from "./page/page.effect";
import { PreloadEffect } from "./preload/preload.effect";
import { CasinoBlockEffect } from "./responsible-gaming/casino-block/casino-block.effect";
import { DepositLimitRequiredEffect } from "./responsible-gaming/deposit-limit/deposit-limit-required/deposit-limit-required.effect";
import { DepositLimitEffect } from "./responsible-gaming/deposit-limit/deposit-limit.effect";
import { MultiDepositLimitEffect } from "./responsible-gaming/deposit-limit/multi-deposit-limit/multi-deposit-limit.effect";
import { ProductBlockEffect } from "./responsible-gaming/product-block/product-block.effect";
import { RealityCheckEffect } from "./responsible-gaming/reality-check/reality-check.effect";
import { ResponsibleGamingEffect } from "./responsible-gaming/responsible-gaming.effect";
import { SessionSummaryEffect } from "./responsible-gaming/session-summary/session-summary.effect";
import { SportsbookBlockEffect } from "./responsible-gaming/sportsbook-block/sportsbook-block.effect";
import { ClientStatusCodeService, StatusCodeService } from "./status-code/status-code.service";
import { AuthTelemetryEffect } from "./telemetry/auth/auth-telemetry.effect";
import { AuthTransactionTelemetryEffect } from "./telemetry/auth-transaction/auth-transaction-telemetry.effect";
import { DgaAuthTransactionTelemetryEffect } from "./telemetry/auth-transaction/dga-auth-transaction-telemetry.effect";
import { RouteTelemetryEffect } from "./telemetry/route/route-telemetry.effect";
import { CORE_TRACKING_DATA } from "./telemetry/tracking";
import { TrackingAreaService } from "./telemetry/tracking-area/tracking-area.service";
import { UiInteractionTelemetryEffect } from "./telemetry/ui-interaction/ui-interaction-telemetry.effect";
import { WalletEffect } from "./wallet/wallet.effect";

const JURISDICTION_EFFECTS = [JurisdictionEffect];

const RESPONSIBLE_GAMING_EFFECTS = [
	DepositLimitEffect,
	MultiDepositLimitEffect,
	ResponsibleGamingEffect,
	RealityCheckEffect,
	ProductBlockEffect,
	CasinoBlockEffect,
	SportsbookBlockEffect,
	SessionSummaryEffect,
];

const CORE_TELEMETRY_EFFECTS = [
	AuthTelemetryEffect,
	AuthTransactionTelemetryEffect,
	DgaAuthTransactionTelemetryEffect,
	RouteTelemetryEffect,
	UiInteractionTelemetryEffect,
	JurisdictionTelemetryEffect,
];

@NgModule({
	imports: [
		GlobalModule,
		NgrxUtilsModule,
		RouterModule,
		EffectsModule.forFeature([
			AamsDataEffect,
			AuthTransactionEffect,
			DgaAuthTransactionEffect,
			GoogleAuthTransactionEffect,
			SmartIdAuthTransactionEffect,
			AppContextEffect,
			CurrencyEffect,
			CustomerManagementEffect,
			DocumentMetaEffect,
			KycActionsEffect,
			CustomerComplianceEffect,
			DepositLimitRequiredEffect,
			LastVisitedProductEffect,
			PageEffect,
			PreloadEffect,
			WalletEffect,
			SessionEffect,
			LimitsReminderEffect,
			GameSessionEffect,
			...CORE_TELEMETRY_EFFECTS,
			...JURISDICTION_EFFECTS,
			...RESPONSIBLE_GAMING_EFFECTS,
		]),
		JurisdictionPrefixModule,
	],
	declarations: [CommandDispatcherDirective],
	exports: [GlobalModule, CommandDispatcherDirective, JurisdictionPrefixModule],
})
export class CoreModule {
	/**
	 * Call `CoreModule.forRoot` in your application module.
	 *
	 * @param config Config is required, specify it in `forRoot` or alternatively using `CORE_CONFIG` token to utilize DI.
	 */
	static forRoot(config?: CoreConfigModel | (() => CoreConfigModel)): ModuleWithProviders<CoreModule> {
		return {
			ngModule: CoreModule,
			providers: [
				OpenIdConnectService,
				{ provide: StatusCodeService, useClass: ClientStatusCodeService },
				{ provide: CORE_CONFIG, useFactory: _coreConfigFactory, deps: [_CORE_CONFIG] },
				{ provide: _CORE_CONFIG, useValue: config },
				{
					provide: CustomerManagementService,
					useFactory: _customerManagementServiceFactory,
					deps: [CORE_CONFIG, CoreHttpClient, CustomerManagementConfigService],
				},
				{
					provide: APP_INITIALIZER,
					multi: true,
					useFactory: _initializeCoreFactory,
					deps: [TrackingAreaService],
				},
			],
		};
	}
}
/**
 * @internal
 */
export function _coreConfigFactory(config: CoreConfigModel | (() => CoreConfigModel)) {
	return isFunction(config) ? config() : config;
}

/**
 * @internal
 */
export function _initializeCoreFactory(trackingAreaService: TrackingAreaService) {
	return _initializeCore.bind(undefined, trackingAreaService);
}

/**
 * @internal
 */
export function _initializeCore(trackingAreaService: TrackingAreaService) {
	trackingAreaService.registerTrackingData(CORE_TRACKING_DATA);
}

/**
 * @internal
 */
export function _customerManagementServiceFactory(
	coreConfig: CoreConfig | undefined,
	httpClient: CoreHttpClient,
	config: CustomerManagementConfigService
): CustomerManagementService {
	return coreConfig?.disableCustomerManagementService
		? new NoopCustomerManagementService()
		: new CoreCustomerManagementService(httpClient, config);
}
